import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { brl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calculator, Check, Search, Ruler } from "lucide-react";
import { toast } from "sonner";
import { suggestedPrice, isLinear, sortTiers } from "@/lib/linearPricing";
import { LinearCalcSummary } from "@/components/LinearServiceDialog";

const PricingRow = ({ row, onChange, onSave }) => {
  const sug = suggestedPrice(row);
  const totalCost = Number(row.cost_price || 0) + Number(row.freight_cost || 0) + Number(row.other_cost || 0);
  const curMargin = totalCost > 0 ? ((row.price - totalCost) / totalCost) * 100 : 0;
  const field = (k, tid) => (
    <Input type="number" step="0.01" value={row[k] ?? ""} onChange={(e) => onChange(row.id, k, e.target.value)}
      className="h-8 w-24 text-right ml-auto" data-testid={`${tid}-${row.id}`} />
  );
  return (
    <TableRow data-testid={`pricing-row-${row.id}`}>
      <TableCell className="font-medium">{row.name}<div className="text-[10px] text-slate-500">{row.category || ""}</div></TableCell>
      <TableCell className="text-right kpi-num">{brl(row.cost_price)}</TableCell>
      <TableCell className="text-right">{field("freight_cost", "pr-freight")}</TableCell>
      <TableCell className="text-right">{field("other_cost", "pr-other")}</TableCell>
      <TableCell className="text-right kpi-num">{brl(totalCost)}</TableCell>
      <TableCell className="text-right">{field("margin_pct", "pr-margin")}</TableCell>
      <TableCell className="text-right kpi-num font-semibold text-blue-600" data-testid={`pr-suggested-${row.id}`}>{brl(sug)}</TableCell>
      <TableCell className="text-right kpi-num">{brl(row.price)}<div className={"text-[10px] " + (curMargin < Number(row.margin_pct || 0) ? "text-rose-600" : "text-emerald-600")}>{curMargin.toFixed(0)}% s/ custo</div></TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button size="sm" variant="outline" className="mr-1" onClick={() => onSave(row, false)} data-testid={`pr-save-${row.id}`}>Salvar</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={sug <= 0} onClick={() => onSave(row, true)} data-testid={`pr-apply-${row.id}`}>
          <Check className="w-3.5 h-3.5 mr-1" /> Aplicar
        </Button>
      </TableCell>
    </TableRow>
  );
};

const LinearCalculator = ({ services }) => {
  const [sid, setSid] = useState("");
  const [meters, setMeters] = useState("");
  const svc = services.find((s) => s.id === sid);
  return (
    <Card className="card-outline" data-testid="linear-calculator">
      <CardContent className="p-5 space-y-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Ruler className="w-5 h-5 text-blue-600" /> Calculadora de serviços por metro linear</h2>
        {services.length === 0 ? (
          <p className="text-sm text-slate-500">Cadastre um serviço com cobrança por metro linear em Produtos &amp; Serviços.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Serviço</Label>
              <Select value={sid} onValueChange={setSid}>
                <SelectTrigger data-testid="calc-service"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Label>Metragem (m)</Label>
              <Input type="number" step="0.01" value={meters} onChange={(e) => setMeters(e.target.value)} data-testid="calc-meters" />
            </div>
            <div>{svc && <LinearCalcSummary product={svc} meters={meters} />}</div>
            <div className="text-xs text-slate-600 space-y-1">
              {svc?.pricing_mode === "linear_tiers" && sortTiers(svc.tiers).map((t, i) => (
                <div key={`t-${i}`} className="flex justify-between border-b border-slate-100 pb-1">
                  <span>{t.up_to == null ? "Acima" : `Até ${t.up_to} m`}</span><span className="kpi-num">{brl(t.price_per_m)}/m</span>
                </div>
              ))}
              {svc?.pricing_mode === "linear_material" && (
                <div>Material {brl(svc.material_cost_m)}/m + mão de obra {brl(svc.labor_cost_m)}/m + margem {svc.margin_pct || 0}%</div>
              )}
              {svc?.min_charge > 0 && <div>Valor mínimo: {brl(svc.min_charge)}</div>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Pricing() {
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [bulkMargin, setBulkMargin] = useState("");

  const load = useCallback(() => api.get("/products").then((r) => {
    setRows(r.data.filter((p) => p.type === "product").map((p) => ({ ...p, margin_pct: p.margin_pct ?? "" })));
    setServices(r.data.filter((p) => p.type === "service" && isLinear(p)));
  }), []);
  useEffect(() => { load(); }, [load]);

  const onChange = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const onSave = async (row, apply) => {
    try {
      const { data } = await api.patch(`/products/${row.id}/pricing`, {
        freight_cost: Number(row.freight_cost) || 0, other_cost: Number(row.other_cost) || 0,
        margin_pct: row.margin_pct === "" ? null : Number(row.margin_pct),
        price: apply ? suggestedPrice(row) : null,
      });
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...data, margin_pct: data.margin_pct ?? "" } : r)));
      toast.success(apply ? `Preço atualizado para ${brl(data.price)}` : "Precificação salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const filtered = useMemo(() => rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.category || "").toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const applyBulk = () => {
    if (bulkMargin === "") return;
    const ids = new Set(filtered.map((r) => r.id));
    setRows((rs) => rs.map((r) => (ids.has(r.id) ? { ...r, margin_pct: bulkMargin } : r)));
    toast.info("Margem preenchida nos itens filtrados. Clique em Salvar ou Aplicar em cada item.");
  };

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Catálogo</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><Calculator className="w-7 h-7" /> Tabela de Precificação</h1>
        <p className="text-sm text-slate-500 mt-1">Preço sugerido = (custo + frete por unidade + outros custos por unidade) × (1 + margem %).</p>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar produto ou categoria..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pricing-search" />
        </div>
        <div className="flex gap-2 items-end">
          <div><Label className="text-xs">Margem % para os filtrados</Label>
            <Input type="number" className="w-28" value={bulkMargin} onChange={(e) => setBulkMargin(e.target.value)} data-testid="bulk-margin-input" /></div>
          <Button variant="outline" onClick={applyBulk} data-testid="bulk-margin-btn">Preencher</Button>
        </div>
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Produto</TableHead><TableHead className="text-right">Custo</TableHead>
              <TableHead className="text-right">Frete/un</TableHead><TableHead className="text-right">Outros/un</TableHead>
              <TableHead className="text-right">Custo total</TableHead><TableHead className="text-right">Margem %</TableHead>
              <TableHead className="text-right">Sugerido</TableHead><TableHead className="text-right">Preço atual</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((r) => <PricingRow key={r.id} row={r} onChange={onChange} onSave={onSave} />)}
              {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">Nenhum produto.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <LinearCalculator services={services} />
    </div>
  );
}
